import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, ArrowLeft } from 'lucide-react';
import { CookieBanner } from '../components/CookieBanner';

// Simple public layout for policy pages
function PublicLayout({ children }) {
  return (
    <div className="theme-dark-scope min-h-screen bg-obsidian">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-kraveon.svg" alt="Kraveon" className="w-9 h-9 rounded-lg" />
            <span className="font-heading text-lg tracking-tight">KRAVEON</span>
          </Link>
          <Link to="/login" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Torna al login
          </Link>
        </div>
      </header>
      <main className="pb-20">
        {children}
      </main>
      <footer className="fixed bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm border-t border-border py-2 z-30">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-xs text-muted-foreground">
          <span>© 2026 Kraveon</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-volt transition-colors">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="hover:text-volt transition-colors">
              Cookie Policy
            </Link>
            <button 
              onClick={() => window.openCookieSettings?.()}
              className="hover:text-volt transition-colors"
            >
              Gestisci Cookie
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function PrivacyPolicy() {
  const { language } = useLanguage();

  const content = language === 'it' ? {
    title: 'Informativa sulla Privacy',
    lastUpdate: 'Ultimo aggiornamento: Marzo 2026',
    sections: [
      {
        title: '1. Titolare del Trattamento',
        content: `Il Titolare del trattamento dei dati personali è Kraveon ("noi", "nostro" o "Titolare"), contattabile all'indirizzo email: privacy@kraveon.com

Ai sensi del Regolamento UE 2016/679 (GDPR) e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018, questa informativa descrive le modalità di trattamento dei dati personali degli utenti che utilizzano la nostra piattaforma.`
      },
      {
        title: '2. Dati Personali Raccolti',
        content: `Raccogliamo le seguenti categorie di dati personali:

• Dati identificativi: nome, cognome, indirizzo email
• Dati di accesso: credenziali di autenticazione (password criptate)
• Dati di utilizzo: log di accesso, preferenze, interazioni con la piattaforma
• Dati di allenamento: programmi, risultati workout, record personali, misurazioni corporee
• Dati di comunicazione: messaggi scambiati tra coach e atleti
• Dati tecnici: indirizzo IP, tipo di browser, dispositivo utilizzato

I dati sono raccolti direttamente dall'utente durante la registrazione e l'utilizzo del servizio.`
      },
      {
        title: '3. Finalità e Base Giuridica del Trattamento',
        content: `I dati personali sono trattati per le seguenti finalità:

a) Erogazione del servizio (art. 6.1.b GDPR - esecuzione contratto)
   - Creazione e gestione account
   - Erogazione programmi di allenamento
   - Tracciamento progressi e risultati
   - Comunicazione coach-atleta

b) Obblighi legali (art. 6.1.c GDPR)
   - Adempimenti fiscali e contabili
   - Risposta a richieste delle autorità

c) Legittimo interesse (art. 6.1.f GDPR)
   - Sicurezza della piattaforma
   - Prevenzione frodi
   - Miglioramento del servizio

d) Consenso esplicito (art. 6.1.a GDPR)
   - Invio comunicazioni promozionali
   - Cookie di profilazione
   - Condivisione dati con terze parti per marketing`
      },
      {
        title: '4. Periodo di Conservazione',
        content: `I dati personali sono conservati per il tempo necessario al conseguimento delle finalità per cui sono stati raccolti:

• Dati account: per tutta la durata del rapporto contrattuale e per 10 anni dopo la cessazione (obblighi fiscali)
• Dati di allenamento: per tutta la durata dell'account e 2 anni dopo la cancellazione
• Log di accesso: 6 mesi
• Consensi cookie: 6 mesi, poi richiesto nuovo consenso
• Comunicazioni: fino a cancellazione da parte dell'utente

Dopo tali periodi, i dati saranno cancellati o anonimizzati.`
      },
      {
        title: '5. Destinatari dei Dati',
        content: `I dati personali possono essere comunicati a:

• Fornitori di servizi tecnici (hosting, email, analytics) in qualità di Responsabili del trattamento
• Autorità competenti su richiesta legittima
• Coach assegnati (per atleti) - dati di allenamento, risultati e, se l'atleta ha attivato il diario di salute prestandovi esplicito consenso, i parametri di salute e recupero che vi registra
• Coach del team a cui l'atleta è assegnato, o che gestiscono un gruppo di cui fa parte - gli stessi dati dei coach assegnati, perché seguono l'atleta allo stesso titolo

Non vendiamo né condividiamo i dati personali con terze parti per finalità di marketing senza esplicito consenso.

Trasferimenti extra-UE: qualora necessario, avvengono solo verso paesi con decisione di adeguatezza o con garanzie appropriate (Clausole Contrattuali Standard).`
      },
      {
        title: '6. Diritti dell\'Interessato',
        content: `Ai sensi degli artt. 15-22 GDPR, l'utente ha diritto di:

• Accesso: ottenere conferma del trattamento e copia dei dati
• Rettifica: correggere dati inesatti o incompleti
• Cancellazione ("diritto all'oblio"): richiedere la cancellazione dei dati
• Limitazione: limitare il trattamento in determinati casi
• Portabilità: ricevere i dati in formato strutturato
• Opposizione: opporsi al trattamento per legittimo interesse
• Revoca del consenso: in qualsiasi momento, senza pregiudicare la liceità del trattamento precedente

Per esercitare i diritti: privacy@kraveon.com

Diritto di reclamo: è possibile presentare reclamo al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it).`
      },
      {
        title: '7. Sicurezza dei Dati',
        content: `Adottiamo misure tecniche e organizzative appropriate per proteggere i dati personali:

• Crittografia delle password (bcrypt)
• Connessioni HTTPS
• Accesso limitato ai dati su base "need to know"
• Backup regolari
• Monitoraggio accessi

In caso di violazione dei dati (data breach), notificheremo il Garante entro 72 ore e, se necessario, gli interessati.`
      },
      {
        title: '8. Cookie',
        content: `Per informazioni dettagliate sui cookie utilizzati, consulta la nostra Cookie Policy accessibile dal footer del sito.`
      },
      {
        title: '9. Modifiche all\'Informativa',
        content: `Ci riserviamo il diritto di modificare questa informativa. Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento. Per modifiche sostanziali, invieremo una notifica agli utenti registrati.`
      },
      {
        title: '10. Contatti',
        content: `Per qualsiasi domanda relativa al trattamento dei dati personali:

Email: privacy@kraveon.com
Indirizzo: [Inserire indirizzo legale]

Data Protection Officer (se nominato): dpo@kraveon.com`
      }
    ]
  } : {
    title: 'Privacy Policy',
    lastUpdate: 'Last updated: March 2026',
    sections: [
      {
        title: '1. Data Controller',
        content: `The Data Controller is Kraveon ("we", "our" or "Controller"), contactable at: privacy@kraveon.com

In accordance with EU Regulation 2016/679 (GDPR), this policy describes how we process personal data of users who use our platform.`
      },
      {
        title: '2. Personal Data Collected',
        content: `We collect the following categories of personal data:

• Identity data: name, surname, email address
• Access data: authentication credentials (encrypted passwords)
• Usage data: access logs, preferences, platform interactions
• Training data: programs, workout results, personal records, body measurements
• Communication data: messages exchanged between coaches and athletes
• Technical data: IP address, browser type, device used

Data is collected directly from the user during registration and service use.`
      },
      {
        title: '3. Purposes and Legal Basis',
        content: `Personal data is processed for the following purposes:

a) Service provision (Art. 6.1.b GDPR - contract execution)
   - Account creation and management
   - Training program delivery
   - Progress and results tracking
   - Coach-athlete communication

b) Legal obligations (Art. 6.1.c GDPR)
   - Tax and accounting requirements
   - Response to authority requests

c) Legitimate interest (Art. 6.1.f GDPR)
   - Platform security
   - Fraud prevention
   - Service improvement

d) Explicit consent (Art. 6.1.a GDPR)
   - Marketing communications
   - Profiling cookies
   - Third-party data sharing for marketing`
      },
      {
        title: '4. Data Retention',
        content: `Personal data is retained for the time necessary to achieve the purposes for which it was collected:

• Account data: for the duration of the contract and 10 years after termination
• Training data: for the duration of the account and 2 years after deletion
• Access logs: 6 months
• Cookie consents: 6 months, then new consent required
• Communications: until deleted by user

After these periods, data will be deleted or anonymized.`
      },
      {
        title: '5. Data Recipients',
        content: `Personal data may be shared with:

• Technical service providers (hosting, email, analytics) as Data Processors
• Competent authorities upon legitimate request
• Assigned coaches (for athletes) - training data, results and, if the athlete has enabled the health diary and given explicit consent, the health and recovery indicators recorded in it
• Coaches of the team the athlete is assigned to, or who manage a group the athlete belongs to - the same data as assigned coaches, because they follow the athlete on the same basis

We do not sell or share personal data with third parties for marketing without explicit consent.

Extra-EU transfers: where necessary, only to countries with adequacy decisions or appropriate safeguards.`
      },
      {
        title: '6. Your Rights',
        content: `Under Articles 15-22 GDPR, you have the right to:

• Access: obtain confirmation of processing and a copy of your data
• Rectification: correct inaccurate or incomplete data
• Erasure ("right to be forgotten"): request data deletion
• Restriction: limit processing in certain cases
• Portability: receive data in structured format
• Object: object to processing based on legitimate interest
• Withdraw consent: at any time

To exercise your rights: privacy@kraveon.com

Right to complain: you may file a complaint with the Data Protection Authority.`
      },
      {
        title: '7. Data Security',
        content: `We implement appropriate technical and organizational measures to protect personal data:

• Password encryption (bcrypt)
• HTTPS connections
• Limited data access on "need to know" basis
• Regular backups
• Access monitoring

In case of data breach, we will notify the Authority within 72 hours and, if necessary, affected individuals.`
      },
      {
        title: '8. Cookies',
        content: `For detailed information about cookies used, please see our Cookie Policy accessible from the site footer.`
      },
      {
        title: '9. Policy Changes',
        content: `We reserve the right to modify this policy. Changes will be posted on this page with the update date. For substantial changes, we will notify registered users.`
      },
      {
        title: '10. Contact',
        content: `For any questions regarding personal data processing:

Email: privacy@kraveon.com

Data Protection Officer (if appointed): dpo@kraveon.com`
      }
    ]
  };

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3">
            <Shield className="w-8 h-8 text-volt" />
            {content.title}
          </h1>
          <p className="text-muted-foreground">{content.lastUpdate}</p>
        </div>

        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-6">
            <ScrollArea className="h-[600px] pr-4">
              <div className="space-y-8">
                {content.sections.map((section, index) => (
                  <div key={index}>
                    <h2 className="font-heading text-xl text-volt mb-3">{section.title}</h2>
                    <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
